<script setup lang="ts">
  import { ref, inject, watch, type Ref, onMounted } from 'vue'
  import VideoCard from '@/components/VideoCard.vue'
  import OnboardingSlide from './OnboardingSlide.vue'
  import OnboardingSlideHeadline from './OnboardingSlideHeadline.vue'
  import OnboardingSlideContent from './OnboardingSlideContent.vue'

  const props = withDefaults(
    defineProps<{
      src: string
      headline?: string
      autoplay?: boolean
      loop?: boolean
      muted?: boolean
      controls?: boolean
      slideIndex?: number
    }>(),
    {
      headline: undefined,
      autoplay: true,
      loop: false,
      muted: true,
      controls: false,
      slideIndex: undefined,
    },
  )

  const emit = defineEmits<{
    loading: [loading: boolean]
    progress: [progress: number]
    completed: []
    error: [error: Event]
  }>()

  const videoCardRef = ref()
  // Inject the active slide from parent OnboardingSliderPage
  const activeSlide = inject<Ref<number>>('activeSlide', ref(-1))

  // Watch for slide changes to auto play/pause
  watch(activeSlide, (currentSlide) => {
    if (props.slideIndex !== undefined && videoCardRef.value) {
      if (currentSlide === props.slideIndex) {
        if (props.autoplay) {
          videoCardRef.value.play()
        }
      } else {
        videoCardRef.value.pause()

        // Reset video to start after a delay, but only if still not playing
        setTimeout(() => {
          if (videoCardRef.value && activeSlide.value !== props.slideIndex) {
            videoCardRef.value.resetToStart()
          }
        }, 500)
      }
    }
  })

  const onLoading = (loading: boolean) => {
    emit('loading', loading)
  }

  const onProgress = (progress: number) => {
    emit('progress', progress)
  }

  const onCompleted = () => {
    emit('completed')
  }

  const onError = (error: Event) => {
    emit('error', error)
  }

  // Expose play and pause methods
  const play = () => {
    if (videoCardRef.value) {
      videoCardRef.value.play()
    }
  }

  const pause = () => {
    if (videoCardRef.value) {
      videoCardRef.value.pause()
    }
  }

  const resetToStart = () => {
    if (videoCardRef.value) {
      videoCardRef.value.resetToStart()
    }
  }

  defineExpose({
    play,
    pause,
    resetToStart,
  })

  onMounted(() => {
    // Auto play if this is the first slide
    if (props.slideIndex === 0 && props.autoplay && videoCardRef.value) {
      videoCardRef.value.play()
    }
  })
</script>
<template>
  <onboarding-slide data-is-video="true">
    <onboarding-slide-headline v-if="props.headline">{{
      props.headline
    }}</onboarding-slide-headline>
    <onboarding-slide-content>
      <video-card
        ref="videoCardRef"
        id="video-card"
        :src="props.src"
        :loop="props.loop"
        :muted="props.muted"
        :controls="props.controls"
        @loading="onLoading"
        @progress="onProgress"
        @completed="onCompleted"
        @error="onError"
      ></video-card>
    </onboarding-slide-content>
  </onboarding-slide>
</template>
<style scoped>
  @media (min-aspect-ratio: 15/23) {
    #video-card {
      aspect-ratio: 9/16;
      height: 100%;
      width: auto;
      margin: auto;
    }
  }
</style>
